import { useCallback } from 'react';
import { runWithAuditReason } from '../../../api/client';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { isReasonRequired } from '../../../utils/auditConfig';
import { DAILY_QC_FORM, deleteEntry, errorText } from './dailyQcApi';

/**
 * Deletes Daily QC entries through the shared delete flow. The prototype requires a
 * reason on EVERY delete (the API refuses one without X-Audit-Reason): when Settings →
 * Audit Trail Configuration already requires a reason for "Daily QC", the shared flow asks
 * for it; otherwise it is asked here first. Returns true when the entries were deleted.
 */
export function useDailyQcDelete() {
  const { prompt } = useConfirm();
  const runDelete = useDeleteFlow();
  const notify = useNotify();

  return useCallback(async (oids: string[]): Promise<boolean> => {
    if (oids.length === 0) return false;
    const what = `${oids.length} entr${oids.length === 1 ? 'y' : 'ies'}`;
    let reason: string | null = null;
    if (!isReasonRequired(DAILY_QC_FORM)) {
      reason = await prompt({
        title: 'Delete', message: `Delete ${what}? A reason is required for the audit trail.`,
        placeholder: 'Reason for deletion', required: true, confirmLabel: 'Continue',
      });
      if (reason === null || !reason.trim()) return false;
    }
    return runDelete({
      formName: DAILY_QC_FORM,
      message: `Delete ${what}?`,
      delete: async () => {
        for (const oid of oids) {
          if (reason) await runWithAuditReason(reason, () => deleteEntry(oid));
          else await deleteEntry(oid);
        }
      },
      onError: (e) => notify.error(errorText(e, 'Delete failed.')),
    });
  }, [prompt, runDelete, notify]);
}
